import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import {
  PiArrowRightLight,
  PiArrowsClockwiseLight,
  PiBookOpenTextLight,
  PiBriefcaseLight,
  PiCalendarCheckLight,
  PiCaretRightLight,
  PiChartBarLight,
  PiChartLineUpLight,
  PiCheckCircleLight,
  PiClipboardTextLight,
  PiCircleNotchLight,
  PiCompassLight,
  PiFunnelLight,
  PiGearSixLight,
  PiInfoLight,
  PiLifebuoyLight,
  PiLightbulbLight,
  PiMagnifyingGlassLight,
  PiPaperclipLight,
  PiPaperPlaneTiltLight,
  PiPulseLight,
  PiQuestionLight,
  PiShieldCheckLight,
  PiSignatureLight,
  PiSquaresFourLight,
  PiTargetLight,
  PiTicketLight,
  PiUserGearLight,
  PiUserLight,
  PiUsersLight,
  PiWarningCircleLight
} from 'react-icons/pi';
import { supportTicketAPI } from '../../services/api';

const dashboardSections = [
  {
    title: 'Top greeting and time range',
    icon: PiSquaresFourLight,
    text: 'This is the top strip of the dashboard. It tells you which company workspace you are in, when the data was last updated, and the time range being used.',
    steps: [
      'Check the company name first so you know you are in the right workspace.',
      'Look at the updated time. If it is old, refresh the page.',
      'Use 30D, 90D, or 1Y to change how much history the charts should show.'
    ],
    watch: 'Changing the time range changes charts and reports. It does not delete or edit any data.'
  },
  {
    title: 'Command search',
    icon: PiMagnifyingGlassLight,
    text: 'This search opens a command box. It helps you jump to pages and find jobs or candidates quickly.',
    steps: [
      'Click the search box at the top of Dashboard.',
      'Type a job title, candidate name, or page name.',
      'Choose the result you want, or use the keyboard arrows and Enter.'
    ],
    watch: 'This is a navigation and lookup tool. It is not a public search engine.'
  },
  {
    title: 'Profile completion banner',
    icon: PiShieldCheckLight,
    text: 'If your company profile still needs answers, the dashboard shows a banner. Those answers help the matching engine understand your company.',
    steps: [
      'Click the banner when it appears.',
      'Answer the company culture, work model, support, benefits, and hiring-style questions.',
      'Save your answers so future matches can use them.'
    ],
    watch: 'A weak profile gives the engine less context. Complete it before judging match quality.'
  },
  {
    title: 'Top metric cards',
    icon: PiChartBarLight,
    text: 'The four cards show the most important numbers: active jobs, talent matches, average match score, and offer acceptance.',
    steps: [
      'Read the card number first.',
      'Read the small detail under it to understand what the number means.',
      'Click the card when you want to open the related page.'
    ],
    watch: 'A high number is not always good by itself. For example, many matches still need review.'
  },
  {
    title: 'Talent Activity chart',
    icon: PiChartLineUpLight,
    text: 'This chart shows how matches, interviews, and offers changed over time.',
    steps: [
      'Use it to see whether hiring activity is growing or slowing down.',
      'Compare matches with interviews. If matches are high but interviews are low, review candidate fit or your follow-up speed.',
      'Compare interviews with offers. If interviews are high but offers are low, review interview outcomes or role expectations.'
    ],
    watch: 'The chart only becomes useful after jobs receive matches and workflow activity.'
  },
  {
    title: 'Match Quality chart',
    icon: PiTargetLight,
    text: 'This chart groups matches by score band. It helps you see whether the system is finding strong, medium, or weak matches.',
    steps: [
      'Look at the average score in the middle.',
      'Check how many matches fall into stronger bands.',
      'Open Candidates or Matching bands if the quality looks lower than expected.'
    ],
    watch: 'Low bands can mean the job is too broad, too strict, missing details, or the candidate pool is still small.'
  },
  {
    title: 'Job Creation Trend',
    icon: PiBriefcaseLight,
    text: 'This line shows how many jobs your company created over time.',
    steps: [
      'Use it to understand hiring activity.',
      'If the line is flat and you expected new roles, check the Jobs page.',
      'Use it with Talent Activity to see whether new jobs are producing matches.'
    ],
    watch: 'Creating many jobs is not enough. Each job still needs clear details to match well.'
  },
  {
    title: 'Candidate Locations',
    icon: PiUsersLight,
    text: 'This chart shows where matched candidates are located when location data is available.',
    steps: [
      'Use it to see whether candidates are in the regions you expect.',
      'If the locations look wrong, check the job location and remote-work setting.',
      'Use this before deciding whether a role should be remote, hybrid, or location-specific.'
    ],
    watch: 'Some candidates may not have full location data, so this is a guide, not a complete map.'
  },
  {
    title: 'Conversion Funnel',
    icon: PiFunnelLight,
    text: 'The funnel shows how people move from matches to later hiring steps.',
    steps: [
      'Start from the top of the funnel.',
      'Look for the biggest drop.',
      'If many matches do not become interviews, review your matching bands and candidate review process.',
      'If interviews do not become offers, review interview feedback or role expectations.'
    ],
    watch: 'The funnel shows where people drop off. It does not automatically explain why.'
  },
  {
    title: 'Skill Gap Pressure',
    icon: PiWarningCircleLight,
    text: 'This shows skills that repeatedly appear as gaps in your matches.',
    steps: [
      'Read the most repeated missing skills.',
      'Decide whether those skills are truly required or just preferred.',
      'Update the job if the requirements are too strict or unclear.'
    ],
    watch: 'Do not remove important requirements just to increase match count. Use this to clarify the job.'
  },
  {
    title: 'Top Performing Jobs',
    icon: PiBriefcaseLight,
    text: 'This table ranks jobs by match volume, average score, interviews, and offers.',
    steps: [
      'Open jobs with strong scores to understand what is working.',
      'Open jobs with weak activity to improve the description or matching profile.',
      'Use the job row to go straight to the role details.'
    ],
    watch: 'A newer job may look weak simply because it has not had enough time to receive matches.'
  },
  {
    title: 'Recent Matches',
    icon: PiUsersLight,
    text: 'This shows the newest AI-generated matches and their scores.',
    steps: [
      'Click a match to open the related job or candidate flow.',
      'Look at the score and the displayed skills.',
      'Decide whether to express interest, pass, or review more detail.'
    ],
    watch: 'A match score is a starting point. Always read the profile before making a hiring decision.'
  },
  {
    title: 'Matching Readiness',
    icon: PiCheckCircleLight,
    text: 'This card tells you how ready your workspace is for strong matching.',
    steps: [
      'Check the profile strength percentage.',
      'Click View setup if the score is low.',
      'Complete missing company details and matching configuration.'
    ],
    watch: 'Readiness improves future matching context. It does not instantly rewrite past decisions.'
  },
  {
    title: 'Smart Insights',
    icon: PiLightbulbLight,
    text: 'These are system-generated suggestions based on your hiring data.',
    steps: [
      'Read the insight in plain language.',
      'Click the suggested action if it matches what you want to fix.',
      'Use the linked page to make the actual change.'
    ],
    watch: 'Insights are recommendations. You still decide what action is right.'
  },
  {
    title: 'Upcoming Interviews',
    icon: PiCalendarCheckLight,
    text: 'This shows interviews that are scheduled ahead.',
    steps: [
      'Check the candidate, job, match score, and scheduled date.',
      'Open Interviews if you need more detail.',
      'Follow up if an interview is close and still needs confirmation.'
    ],
    watch: 'Always check date and time carefully, especially if teams are in different time zones.'
  },
  {
    title: 'Recent Activity',
    icon: PiPulseLight,
    text: 'This is a live-style feed of recent jobs, matches, offers, and other platform activity.',
    steps: [
      'Use it to understand what changed recently.',
      'Open the related page if something needs action.',
      'Use it together with reports when explaining recent progress.'
    ],
    watch: 'Recent activity is not a full audit log. It is a quick dashboard feed.'
  }
];

const guideSections = [
  {
    key: 'dashboard',
    name: 'Dashboard',
    icon: PiSquaresFourLight,
    summary: 'Your daily starting point. It shows your hiring health, matching progress, upcoming work, and useful signals.',
    pages: [
      {
        title: 'Dashboard overview',
        path: '/dashboard',
        icon: PiSquaresFourLight,
        simple: 'Use Dashboard when you want a quick answer to: How is our hiring going today?',
        useFor: [
          'See active jobs, matches, average match score, offer acceptance, and upcoming interviews.',
          'Check whether matching is producing good results.',
          'Spot jobs or hiring steps that need attention.',
          'Open important pages from one place.'
        ],
        steps: [
          'Open Dashboard from the sidebar.',
          'Start with the four top cards.',
          'Check the charts to see trends and quality.',
          'Look at Matching Readiness to see if your company setup is helping the engine.',
          'Review Smart Insights and Upcoming Interviews before leaving the page.'
        ],
        careful: [
          'Dashboard is a summary. Open the detailed page before making a final decision.',
          'Empty sections usually mean the system has not received enough jobs, matches, interviews, or offers yet.'
        ]
      },
      {
        title: 'Dashboard sections',
        path: '/dashboard',
        icon: PiCompassLight,
        simple: 'These are the blocks inside the Dashboard page. Each block answers a different question.',
        custom: 'dashboard-sections',
        useFor: [],
        steps: [],
        careful: []
      }
    ]
  },
  {
    key: 'jobs',
    name: 'Jobs',
    icon: PiBriefcaseLight,
    summary: 'Jobs are the roles you want to hire for. Strong job details create stronger matches.',
    pages: [
      {
        title: 'Jobs',
        path: '/jobs',
        icon: PiBriefcaseLight,
        simple: 'This page lists your roles and shows whether each job is draft, active, closed, or still being prepared by AI.',
        useFor: [
          'See all jobs in one place.',
          'Open a job to review or edit it.',
          'Publish a draft when it is ready.',
          'Close or pause an active job when you no longer want matches.',
          'Open the matches for a specific job.'
        ],
        steps: [
          'Open Jobs from the sidebar.',
          'Use filters or search to find the role.',
          'Check the job status and AI drafting badge.',
          'Open the job before publishing if you have not reviewed the details.',
          'Use the matches button when you are ready to review candidates for that role.'
        ],
        careful: [
          'Do not publish a job before reviewing extracted or typed details.',
          'If AI drafting is still running, wait or keep editing only the fields you understand.'
        ]
      },
      {
        title: 'Create job',
        path: '/jobs/create',
        icon: PiBriefcaseLight,
        simple: 'This page helps you create a role. You can upload a job description or type the role manually.',
        useFor: [
          'Upload a JD file so the system can create a secure draft.',
          'Let AI extract role, requirements, and compensation details.',
          'Review each extracted section before publishing.',
          'Answer job-specific matching questions.',
          'Publish the job and start matching.'
        ],
        steps: [
          'Open Jobs, then choose Create job.',
          'Drop a JD file into the upload area or choose manual entry.',
          'Wait for the draft to be created. You can continue while AI finishes drafting.',
          'Review role details, requirements, and compensation.',
          'Answer matching profile questions or skip only when the answer is truly not needed.',
          'Choose whether to publish and match immediately or save as draft.',
          'After publishing, open the matches page to review candidates.'
        ],
        careful: [
          'The system converts compensation to USD for matching. Review converted salary before publishing.',
          'AI can help draft the job, but you are responsible for confirming that the job is accurate.'
        ]
      },
      {
        title: 'Job details',
        path: '/jobs',
        icon: PiInfoLight,
        simple: 'This page shows one job in detail.',
        useFor: [
          'Review title, description, requirements, compensation, job status, and profile strength.',
          'Edit job information.',
          'Publish or pause the role.',
          'Answer more matching profile questions.',
          'Open matches for that job.'
        ],
        steps: [
          'Open Jobs.',
          'Click the job you want to inspect.',
          'Read the job summary and status.',
          'Edit fields if something is wrong or missing.',
          'Complete unanswered matching questions to improve fit.',
          'Publish when ready, or open matches if it is already active.'
        ],
        careful: [
          'Changing a job can change future matching behavior.',
          'If a job already has matches, review the effect before making major requirement changes.'
        ]
      },
      {
        title: 'Job matches',
        path: '/jobs',
        icon: PiTargetLight,
        simple: 'This page shows candidates matched to one job.',
        useFor: [
          'Review candidates for a specific role.',
          'See match score, band, strengths, gaps, and explanation details.',
          'Express interest in a candidate.',
          'Pass on a candidate and record feedback where the flow asks for it.',
          'Move a mutual match toward interview or offer.'
        ],
        steps: [
          'Open a job, then open its matches.',
          'Start with higher-score and stronger-band matches.',
          'Open the candidate detail before acting.',
          'Express interest when the candidate looks worth continuing.',
          'Pass when the candidate is not a fit, and give a useful reason if asked.',
          'Schedule an interview when interest is mutual.'
        ],
        careful: [
          'Do not rely only on the score. Read the candidate evidence and gaps.',
          'Feedback should be professional and useful. It may help improve the candidate profile experience.'
        ]
      }
    ]
  },
  {
    key: 'candidates',
    name: 'Candidates',
    icon: PiUsersLight,
    summary: 'This is where you review people who match your roles and move them through the hiring flow.',
    pages: [
      {
        title: 'Candidates',
        path: '/candidates',
        icon: PiUsersLight,
        simple: 'This page is your match pipeline across jobs.',
        useFor: [
          'See candidates matched to your roles.',
          'Filter by match status, band, candidate action, job, and score.',
          'Open candidate profiles.',
          'Express interest or pass.',
          'Schedule interviews when both sides show interest.'
        ],
        steps: [
          'Open Candidates from the sidebar.',
          'Use filters to narrow the list.',
          'Read the match score, band, job, workflow count, skills, and profile completion.',
          'Open the candidate profile before taking a major action.',
          'Express interest if you want the candidate to know.',
          'Use Pass only when you are sure the candidate is not right for that role.'
        ],
        careful: [
          'Candidate contact details may stay hidden until mutual interest.',
          'Passing should be thoughtful. The system may use feedback to guide the candidate.'
        ]
      },
      {
        title: 'Candidate profile',
        path: '/candidates',
        icon: PiUserLight,
        simple: 'This page gives a deeper view of one candidate and their fit for your job.',
        useFor: [
          'Review profile details, experience, education, skills, preferences, and evidence.',
          'Compare the same candidate across jobs when they match more than one role.',
          'Read strengths, risks, and dimension scores.',
          'Take actions like express interest, pass, or schedule interview.'
        ],
        steps: [
          'Open a candidate from Candidates or Job matches.',
          'Read the top summary and match badges.',
          'Review the dossier, decision brief, strengths, risks, and score details.',
          'Check the timeline for interviews, assessments, and offers.',
          'Choose the next action only after reviewing the evidence.'
        ],
        careful: [
          'A candidate may be strong for one job and weak for another. Always check which job you are viewing.',
          'The evidence panel helps explain the match, but human review still matters.'
        ]
      },
      {
        title: 'Matching bands',
        path: '/candidates/bands',
        icon: PiTargetLight,
        simple: 'This page controls which score bands you prefer and how immediate matching behaves.',
        useFor: [
          'Choose preferred score bands.',
          'Turn immediate matching on or off.',
          'Control match notifications and match limits.',
          'Review matching dimensions and weights.'
        ],
        steps: [
          'Open Candidates, then Matching bands when available.',
          'Select the bands you want the system to prioritize.',
          'Review immediate matching and notification toggles.',
          'Set a sensible match limit.',
          'Save the configuration.'
        ],
        careful: [
          'Choosing only very high bands may reduce the number of candidates you see.',
          'Changing matching settings affects future workflow behavior.'
        ]
      }
    ]
  },
  {
    key: 'assessments',
    name: 'Assessments',
    icon: PiClipboardTextLight,
    summary: 'Assessments help you evaluate candidates beyond the CV and match score.',
    pages: [
      {
        title: 'Assessments',
        path: '/assessments',
        icon: PiClipboardTextLight,
        simple: 'This page helps you manage candidate evaluations.',
        useFor: [
          'View assessment records connected to jobs and candidates.',
          'Check status, attempts, scores, questions, and completion.',
          'Understand where an assessment sits in the hiring flow.',
          'Follow up when an assessment is incomplete or needs review.'
        ],
        steps: [
          'Open Assessments from the sidebar.',
          'Search or filter for the job or candidate.',
          'Check whether the assessment is pending, completed, passed, failed, or needs review.',
          'Open details before using the result in a hiring decision.',
          'Use the candidate and job context together with the score.'
        ],
        careful: [
          'Assessment results should support a decision, not replace human judgment.',
          'If an assessment looks wrong, check the candidate profile and workflow history before acting.'
        ]
      }
    ]
  },
  {
    key: 'interviews',
    name: 'Interviews',
    icon: PiCalendarCheckLight,
    summary: 'Interviews help you move from match review to direct conversation.',
    pages: [
      {
        title: 'Interviews',
        path: '/interviews',
        icon: PiCalendarCheckLight,
        simple: 'This page shows interviews and their status.',
        useFor: [
          'View scheduled, pending, completed, cancelled, or failed interviews.',
          'Check candidate, job, time, meeting link, and confirmation state.',
          'Copy meeting links where available.',
          'Follow up on interviews that need attention.'
        ],
        steps: [
          'Open Interviews from the sidebar.',
          'Filter or search for the candidate or job.',
          'Check the date, time, status, and meeting information.',
          'Open details if the interview needs a decision or follow-up.',
          'Update the workflow only when you know the correct outcome.'
        ],
        careful: [
          'Always verify the date and time before contacting a candidate.',
          'Do not schedule interviews unless the candidate and role context are correct.'
        ]
      },
      {
        title: 'Schedule interview',
        path: '/interviews/schedule',
        icon: PiCalendarCheckLight,
        simple: 'This page helps you create an interview for a candidate and job.',
        useFor: [
          'Pick the matched candidate and job.',
          'Set interview type, time, and meeting information.',
          'Send interview details into the workflow.',
          'Continue the process after mutual interest.'
        ],
        steps: [
          'Open Schedule interview from a candidate or interview action.',
          'Confirm the candidate, job, and match.',
          'Choose the interview date and type.',
          'Add the meeting link or instructions.',
          'Save and confirm the interview appears on the Interviews page.'
        ],
        careful: [
          'Scheduling the wrong candidate or job creates confusion for both sides.',
          'Use clear meeting instructions so the candidate knows what to do.'
        ]
      }
    ]
  },
  {
    key: 'offers',
    name: 'Offers',
    icon: PiSignatureLight,
    summary: 'Offers handle the final hiring terms, documents, negotiations, and signing state.',
    pages: [
      {
        title: 'Offers',
        path: '/offers',
        icon: PiSignatureLight,
        simple: 'This page shows all offers and where each one stands.',
        useFor: [
          'See sent, viewed, negotiation, accepted, declined, withdrawn, and expired offers.',
          'Review compensation, start date, documents, expiry, and signature requirements.',
          'Respond to negotiations.',
          'Withdraw an offer when allowed.',
          'Open offer details.'
        ],
        steps: [
          'Open Offers from the sidebar.',
          'Use status filters to find the offer.',
          'Review candidate, job, compensation, expiry, documents, and status.',
          'Open details before responding to negotiation or withdrawing.',
          'Create a new offer only when candidate and job details are correct.'
        ],
        careful: [
          'Offers and contracts are serious documents. Check names, salary, currency, start date, and expiry before sending.',
          'Withdrawing or changing an offer can affect trust with the candidate.'
        ]
      },
      {
        title: 'Create offer',
        path: '/offers/create',
        icon: PiSignatureLight,
        simple: 'This page helps you send an offer to a candidate.',
        useFor: [
          'Choose the candidate and job.',
          'Enter offer title, compensation, start date, terms, and expiry.',
          'Attach offer letters or contracts where supported.',
          'Set whether signing is required and whether negotiation is allowed.'
        ],
        steps: [
          'Open Create offer from the Offers page or candidate flow.',
          'Confirm the candidate and job are correct.',
          'Enter compensation and employment terms carefully.',
          'Attach the right documents.',
          'Review everything before sending.'
        ],
        careful: [
          'Do not send a draft contract by mistake.',
          'Check currency and pay period. Monthly, yearly, and hourly values are not the same.'
        ]
      },
      {
        title: 'Onboarding',
        path: '/offers/onboarding',
        icon: PiCheckCircleLight,
        simple: 'This page supports the steps after an offer is accepted.',
        useFor: [
          'Track post-offer steps.',
          'Review onboarding-related status where available.',
          'Make sure signed documents or next steps are complete.'
        ],
        steps: [
          'Open Offers, then Onboarding when you need post-offer follow-up.',
          'Find the accepted candidate or offer.',
          'Check the next step and document state.',
          'Follow up if something is missing.'
        ],
        careful: [
          'Onboarding may involve private documents. Only open what you need.'
        ]
      }
    ]
  },
  {
    key: 'reports',
    name: 'Reports',
    icon: PiChartLineUpLight,
    summary: 'Reports help you export and compare hiring data across jobs, candidates, interviews, assessments, offers, and activity.',
    pages: [
      {
        title: 'Reports',
        path: '/reports',
        icon: PiChartLineUpLight,
        simple: 'This page turns hiring data into tables, charts, CSV files, and JSON exports.',
        useFor: [
          'Review hiring performance by time range.',
          'Switch between jobs, candidates, interviews, assessments, offers, and activity tabs.',
          'Search loaded report rows.',
          'Export a summary CSV, tab CSV, or full JSON report.',
          'Share performance data with managers.'
        ],
        steps: [
          'Open Reports from the sidebar.',
          'Choose the time range: 7D, 30D, 90D, or 1Y.',
          'Use the tabs to choose the area you want.',
          'Search within loaded rows if needed.',
          'Export only the file you need.'
        ],
        careful: [
          'Report exports may include candidate and hiring data. Share them only with approved people.',
          'If some report sources did not load, the report may be incomplete.'
        ]
      }
    ]
  },
  {
    key: 'configuration',
    name: 'Configuration',
    icon: PiGearSixLight,
    summary: 'Configuration controls matching behavior and employer-side settings.',
    pages: [
      {
        title: 'Configuration',
        path: '/config',
        icon: PiGearSixLight,
        simple: 'This page is where matching and scoring settings are managed.',
        useFor: [
          'Review matching dimensions and weights.',
          'Tune scoring categories and micro-points.',
          'Understand how your employer setup affects matching.',
          'Adjust settings when your hiring strategy changes.'
        ],
        steps: [
          'Open Configuration from the sidebar.',
          'Read helper text before changing a setting.',
          'Change one group of settings at a time.',
          'Save changes.',
          'Watch later matches to see whether the change helped.'
        ],
        careful: [
          'Configuration can affect match quality. Do not change settings casually.',
          'If you are unsure, ask someone who understands the hiring policy before saving.'
        ]
      }
    ]
  },
  {
    key: 'account',
    name: 'Profile and setup',
    icon: PiUserLight,
    summary: 'This is where your company profile, contact details, KYC state, and setup answers live.',
    pages: [
      {
        title: 'Profile',
        path: '/profile',
        icon: PiUserLight,
        simple: 'This page manages company details and the employer profile used by matching.',
        useFor: [
          'Review company details, contact details, benefits, culture, and working model.',
          'Answer employer profile questions.',
          'Check KYC-related guidance or status actions where shown.',
          'Improve the information used to match candidates to your company.'
        ],
        steps: [
          'Open Profile from the sidebar or bottom account area.',
          'Move through the available profile tabs.',
          'Update company details and culture answers.',
          'Save changes before leaving.',
          'Return to Dashboard to see readiness improve when enough profile questions are answered.'
        ],
        careful: [
          'Candidate matching uses this information. Keep it honest and current.',
          'Do not put private internal notes into public-facing company fields.'
        ]
      },
      {
        title: 'Contact and KYC verification',
        path: '/kyc-verification',
        icon: PiShieldCheckLight,
        simple: 'Before full dashboard access, the employer account must verify contact details and complete KYC.',
        useFor: [
          'Verify phone or WhatsApp contact steps.',
          'Upload or review company verification documents.',
          'Resume KYC if it was not completed earlier.',
          'See whether your company verification is pending, approved, rejected, or needs action.'
        ],
        steps: [
          'After registration, complete contact verification when asked.',
          'Open KYC verification when the system redirects you.',
          'Upload the required documents for your company region.',
          'Submit and wait for admin review.',
          'Return to the page if the system asks for corrections.'
        ],
        careful: [
          'Use the correct company country and region so the right documents are requested.',
          'Blurry, expired, or mismatched documents can delay approval.'
        ]
      }
    ]
  },
  {
    key: 'admin',
    name: 'Team access',
    icon: PiUserGearLight,
    summary: 'Super admins can control who from the employer team can use the dashboard and what each person can open.',
    pages: [
      {
        title: 'Roles',
        path: '/roles',
        icon: PiShieldCheckLight,
        simple: 'Roles are permission bundles. They decide what a dashboard user can open.',
        useFor: [
          'Create roles for different team members.',
          'Choose which areas a role can access.',
          'Edit a role when responsibilities change.',
          'Delete unused roles when allowed.'
        ],
        steps: [
          'Open Roles from the sidebar if you are a super admin.',
          'Create a role or open an existing one.',
          'Choose only the permissions that role needs.',
          'Save the role.',
          'Assign the role to users on the Admin RBAC page.'
        ],
        careful: [
          'Give the smallest useful access, especially for offers, configuration, and user management.',
          'Changing a role affects every user assigned to that role.'
        ]
      },
      {
        title: 'Admin RBAC',
        path: '/users',
        icon: PiUserGearLight,
        simple: 'This page manages employer dashboard users.',
        useFor: [
          'Invite team members.',
          'Assign a role to each user.',
          'Enable or disable dashboard access.',
          'Review recent user access changes.'
        ],
        steps: [
          'Open Admin RBAC from the sidebar if you are a super admin.',
          'Click Add user when inviting someone new.',
          'Enter the correct email and role.',
          'Save. The user receives password setup instructions by email.',
          'Disable access when a user should no longer use the dashboard.'
        ],
        careful: [
          'Never share one account across multiple people.',
          'Disable users promptly when someone leaves the hiring team.'
        ]
      }
    ]
  },
  {
    key: 'bottom',
    name: 'Bottom controls',
    icon: PiBookOpenTextLight,
    summary: 'These controls are always at the bottom of the sidebar.',
    pages: [
      {
        title: 'Employer guide',
        path: '/user-guide',
        icon: PiBookOpenTextLight,
        simple: 'This is the page you are reading now.',
        useFor: [
          'Learn what each dashboard section means.',
          'Teach a new employer user how to use Sanspath.',
          'Find safe step-by-step instructions before changing anything.'
        ],
        steps: [
          'Go to the bottom of the sidebar.',
          'Click Guide.',
          'Use the search box to find a page, action, or word.',
          'Read the steps and careful notes before using sensitive features.'
        ],
        careful: [
          'The guide explains the product. It does not replace your company hiring policy.'
        ]
      },
      {
        title: 'Account profile shortcut',
        path: '/profile',
        icon: PiUserLight,
        simple: 'The avatar area takes you to your company or account profile.',
        useFor: [
          'Open profile settings quickly.',
          'Check company and account information.',
          'Return to setup answers that affect matching.'
        ],
        steps: [
          'Look at the bottom of the sidebar.',
          'Click the company or user avatar area.',
          'Review or update profile details.'
        ],
        careful: [
          'Profile changes can affect future matching.'
        ]
      },
      {
        title: 'Sign out',
        path: '/login',
        actionOnly: true,
        icon: PiShieldCheckLight,
        simple: 'This safely ends your dashboard session.',
        useFor: [
          'Protect company and candidate information when you are done.',
          'Leave the dashboard on shared or public computers.'
        ],
        steps: [
          'Go to the bottom of the sidebar.',
          'Click the sign-out icon.',
          'Wait for the login screen.',
          'Close the browser tab if needed.'
        ],
        careful: [
          'Always sign out on shared devices.'
        ]
      }
    ]
  }
];

const principles = [
  {
    title: 'Start with the job',
    text: 'A clear job creates better matches. If the job is weak, the match list will also be weak.'
  },
  {
    title: 'Read before acting',
    text: 'Scores help you sort. They do not replace reading the candidate profile and match evidence.'
  },
  {
    title: 'Keep feedback respectful',
    text: 'When you pass, negotiate, or message through the system, write like a real person will read it.'
  },
  {
    title: 'Protect private data',
    text: 'Candidate profiles, documents, offers, and reports should only be shared with approved people.'
  }
];

const normalize = (value) => String(value || '').toLowerCase();

const ticketCategories = ['support', 'technical', 'account', 'billing', 'kyc', 'matching', 'interview', 'offer', 'bug', 'other'];
const ticketPriorities = ['normal', 'high', 'urgent', 'low'];

const labelize = (value = '') => String(value || 'Not set')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())
  .replace(/\bKyc\b/g, 'KYC');

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const fileLabel = (file) => {
  if (!file) return '';
  const size = file.size ? ` · ${(file.size / 1024 / 1024).toFixed(2)} MB` : '';
  return `${file.name}${size}`;
};

const supportError = (error, fallback) => error?.response?.error || error?.message || fallback;

const statusBadgeClass = (status) => {
  if (status === 'resolved') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'waiting_on_user') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (status === 'in_progress') return 'border-sky-200 bg-sky-50 text-sky-700';
  return 'border-slate-200 bg-slate-50 text-slate-700';
};

const makeId = (value) => String(value || 'section')
  .replace(/[^a-z0-9]+/gi, '-')
  .replace(/^-|-$/g, '')
  .toLowerCase();

const pageSearchText = (section, page) => [
  section.name,
  section.summary,
  page.title,
  page.path,
  page.simple,
  ...(page.useFor || []),
  ...(page.steps || []),
  ...(page.careful || []),
  ...(page.custom === 'dashboard-sections' ? dashboardSections.flatMap((item) => [item.title, item.text, ...(item.steps || []), item.watch]) : [])
].join(' ');

function BulletList({ items, icon: Icon = PiCheckCircleLight, tone = 'default' }) {
  if (!items?.length) return null;

  const toneClass = tone === 'warning'
    ? 'bg-amber-50 text-amber-700 ring-amber-100'
    : 'bg-sky-50 text-sky-700 ring-sky-100';

  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex gap-3 text-sm leading-6 text-slate-600">
          <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ring-1 ${toneClass}`}>
            <Icon size={14} />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StepList({ items }) {
  if (!items?.length) return null;

  return (
    <ol className="space-y-3">
      {items.map((item, index) => (
        <li key={item} className="flex gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0b1f3a] text-xs font-semibold text-white">
            {index + 1}
          </span>
          <span className="pt-0.5 text-sm leading-6 text-slate-600">{item}</span>
        </li>
      ))}
    </ol>
  );
}

function DashboardSectionGuide() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {dashboardSections.map((item) => {
        const Icon = item.icon;
        return (
          <article key={item.title} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                <Icon size={18} />
              </span>
              <div className="min-w-0">
                <h4 className="font-display text-base font-normal text-[#0b1f3a]">{item.title}</h4>
                <p className="mt-1 text-sm leading-6 text-slate-600">{item.text}</p>
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">How to use it</p>
              <div className="mt-3">
                <StepList items={item.steps} />
              </div>
            </div>
            <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50/70 p-3 text-sm leading-6 text-amber-900">
              <span className="font-semibold">Careful: </span>{item.watch}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function GuideCard({ page }) {
  const Icon = page.icon || PiBookOpenTextLight;
  const cardId = `guide-${makeId(page.title)}-${makeId(page.path)}`;

  return (
    <article id={cardId} className="scroll-mt-24 rounded-[22px] border border-slate-200 bg-white shadow-[0_16px_42px_rgba(15,23,42,0.05)]">
      <div className="border-b border-slate-100 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#0b1f3a] text-white">
              <Icon size={22} />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">{page.path}</p>
              <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-[#0b1f3a]">{page.title}</h3>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">{page.simple}</p>
            </div>
          </div>
          {page.actionOnly ? (
            <span className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-500">
              Sidebar action
            </span>
          ) : (
            <Link
              to={page.path}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800"
            >
              Open page
              <PiArrowRightLight size={16} />
            </Link>
          )}
        </div>
      </div>

      {page.custom === 'dashboard-sections' ? (
        <div className="p-5">
          <DashboardSectionGuide />
        </div>
      ) : (
        <div className="grid gap-5 p-5 xl:grid-cols-[0.95fr_1.05fr]">
          <section className="rounded-2xl bg-slate-50 p-4">
            <h4 className="text-sm font-semibold text-[#0b1f3a]">What you can do here</h4>
            <div className="mt-4">
              <BulletList items={page.useFor} />
            </div>
          </section>

          <section className="rounded-2xl bg-slate-50 p-4">
            <h4 className="text-sm font-semibold text-[#0b1f3a]">Step by step</h4>
            <div className="mt-4">
              <StepList items={page.steps} />
            </div>
          </section>

          <section className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4 xl:col-span-2">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-amber-950">
              <PiWarningCircleLight size={17} />
              Be careful
            </h4>
            <div className="mt-4">
              <BulletList items={page.careful} icon={PiWarningCircleLight} tone="warning" />
            </div>
          </section>
        </div>
      )}
    </article>
  );
}

function SupportTicketPanel({ form, file, submitting, tickets, loading, error, onChange, onFileChange, onSubmit, onRefresh }) {
  return (
    <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
      <div className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 ring-1 ring-sky-100">
            <PiLifebuoyLight size={24} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">Support tickets</p>
            <h2 className="mt-1 font-display text-2xl font-normal tracking-tight text-[#0b1f3a]">Need help from Sanspath?</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Create a ticket with the page, issue, priority, and screenshot. The support team can track it through Admin and notify you when it is resolved.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="mt-5 grid gap-4 xl:grid-cols-2">
          <label className="block xl:col-span-2">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Subject</span>
            <input
              value={form.subject}
              onChange={(event) => onChange('subject', event.target.value)}
              required
              maxLength={220}
              placeholder="Briefly describe the issue"
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-50"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Category</span>
            <select
              value={form.category}
              onChange={(event) => onChange('category', event.target.value)}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-50"
            >
              {ticketCategories.map((category) => <option key={category} value={category}>{labelize(category)}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Priority</span>
            <select
              value={form.priority}
              onChange={(event) => onChange('priority', event.target.value)}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-50"
            >
              {ticketPriorities.map((priority) => <option key={priority} value={priority}>{labelize(priority)}</option>)}
            </select>
          </label>
          <label className="block xl:col-span-2">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Details</span>
            <textarea
              value={form.description}
              onChange={(event) => onChange('description', event.target.value)}
              required
              minLength={10}
              placeholder="Include the affected page, expected behavior, actual behavior, and affected job/candidate/offer if relevant."
              className="min-h-[118px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-900 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-50"
            />
          </label>
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 xl:col-span-2">
            <label className="flex cursor-pointer items-center justify-between gap-4">
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-semibold text-[#0b1f3a]"><PiPaperclipLight size={18} /> Screenshot</span>
                <span className="mt-1 block truncate text-xs text-slate-500">{file ? fileLabel(file) : 'PNG, JPG, or PDF up to 8 MB'}</span>
              </span>
              <span className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">Choose file</span>
              <input type="file" accept=".png,.jpg,.jpeg,.pdf" className="sr-only" onChange={(event) => onFileChange(event.target.files?.[0] || null)} />
            </label>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#0b1f3a] px-4 text-sm font-semibold text-white transition hover:bg-[#102b4f] disabled:cursor-not-allowed disabled:opacity-60 xl:col-span-2"
          >
            {submitting ? <PiCircleNotchLight className="animate-spin" size={18} /> : <PiPaperPlaneTiltLight size={18} />}
            Submit ticket
          </button>
        </form>
      </div>

      <div className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Recent tickets</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">Track the latest requests from this employer account.</p>
          </div>
          <button type="button" onClick={onRefresh} className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50 text-slate-500 transition hover:bg-slate-100">
            {loading ? <PiCircleNotchLight className="animate-spin" size={18} /> : <PiArrowsClockwiseLight size={18} />}
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {loading && (
            <>
              <div className="h-20 animate-pulse rounded-2xl bg-slate-100" />
              <div className="h-20 animate-pulse rounded-2xl bg-slate-100" />
            </>
          )}
          {!loading && error && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Tickets could not be loaded.
            </div>
          )}
          {!loading && !error && tickets.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center">
              <PiTicketLight className="mx-auto text-slate-400" size={22} />
              <p className="mt-2 text-sm font-semibold text-[#0b1f3a]">No tickets yet</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Submitted support requests will appear here.</p>
            </div>
          )}
          {!loading && !error && tickets.map((ticket) => (
            <article key={ticket.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{ticket.ticketNumber}</p>
                  <h3 className="mt-1 line-clamp-2 text-sm font-semibold text-[#0b1f3a]">{ticket.subject}</h3>
                </div>
                <span className={`inline-flex shrink-0 rounded-full border px-2 py-1 text-[11px] font-semibold ${statusBadgeClass(ticket.status)}`}>{labelize(ticket.status)}</span>
              </div>
              <p className="mt-3 text-xs text-slate-500">Updated {formatDate(ticket.updatedAt)}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function EmployerGuidePage() {
  const [search, setSearch] = useState('');
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'support',
    priority: 'normal',
    description: ''
  });
  const [ticketFile, setTicketFile] = useState(null);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const query = normalize(search).trim();

  const ticketsQuery = useQuery(
    ['employer-support-tickets'],
    () => supportTicketAPI.list({ status: 'all', limit: 5 }),
    { staleTime: 30000 }
  );

  const filteredSections = useMemo(() => guideSections.map((section) => ({
    ...section,
    pages: section.pages.filter((page) => !query || normalize(pageSearchText(section, page)).includes(query))
  })).filter((section) => section.pages.length), [query]);

  const totalPages = guideSections.reduce((total, section) => total + section.pages.length, 0);
  const visiblePages = filteredSections.reduce((total, section) => total + section.pages.length, 0);

  const updateTicketForm = (field, value) => setTicketForm((current) => ({
    ...current,
    [field]: value
  }));

  const submitTicket = async (event) => {
    event.preventDefault();
    setSubmittingTicket(true);
    try {
      const response = await supportTicketAPI.create(ticketForm, ticketFile);
      toast.success(`Ticket ${response?.ticket?.ticketNumber || ''} created.`.trim());
      setTicketForm({ subject: '', category: 'support', priority: 'normal', description: '' });
      setTicketFile(null);
      await ticketsQuery.refetch();
    } catch (error) {
      toast.error(supportError(error, 'Ticket could not be created.'));
    } finally {
      setSubmittingTicket(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 pt-16 font-sans sm:p-5 sm:pt-5 lg:p-7">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <header className="overflow-hidden rounded-[24px] border border-slate-200 bg-[#0b1f3a] text-white shadow-[0_24px_60px_rgba(11,31,58,0.18)]">
          <div className="grid gap-8 p-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:p-8">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-100">
                <PiBookOpenTextLight size={16} />
                Employer help
              </div>
              <h1 className="mt-5 max-w-4xl font-display text-3xl font-normal tracking-tight sm:text-4xl">
                Employer User Guide
              </h1>
              <p className="mt-4 max-w-4xl text-sm leading-7 text-slate-200 sm:text-base">
                A simple guide for every employer dashboard section. It explains what each page does, how to use it, and what to check before taking action.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/jobs/create"
                  className="inline-flex h-10 items-center gap-2 rounded-xl bg-white px-4 text-sm font-semibold text-[#0b1f3a] transition hover:bg-sky-50"
                >
                  Create a job
                  <PiCaretRightLight size={16} />
                </Link>
                <Link
                  to="/profile?tab=culture&focus=company-questions"
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-semibold text-white transition hover:bg-white/15"
                >
                  Improve profile
                  <PiCaretRightLight size={16} />
                </Link>
              </div>
            </div>

            <div className="rounded-[22px] border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#0b1f3a]">
                  <PiInfoLight size={22} />
                </span>
                <div>
                  <p className="text-sm font-semibold">How to use this guide</p>
                  <p className="mt-1 text-xs leading-5 text-slate-300">Search for a page, read the steps, then open the page when you are ready.</p>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-2xl bg-white/10 p-4">
                  <div className="font-display text-2xl font-normal">{totalPages}</div>
                  <div className="mt-1 text-xs text-slate-300">Guide topics</div>
                </div>
                <div className="rounded-2xl bg-white/10 p-4">
                  <div className="font-display text-2xl font-normal">{dashboardSections.length}</div>
                  <div className="mt-1 text-xs text-slate-300">Dashboard blocks</div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-4">
          {principles.map((item) => (
            <div key={item.title} className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                <PiCheckCircleLight size={19} />
              </span>
              <h2 className="mt-4 font-display text-lg font-normal text-[#0b1f3a]">{item.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{item.text}</p>
            </div>
          ))}
        </section>

        <SupportTicketPanel
          form={ticketForm}
          file={ticketFile}
          submitting={submittingTicket}
          tickets={ticketsQuery.data?.tickets || []}
          loading={ticketsQuery.isLoading}
          error={ticketsQuery.isError}
          onChange={updateTicketForm}
          onFileChange={setTicketFile}
          onSubmit={submitTicket}
          onRefresh={() => ticketsQuery.refetch()}
        />

        <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="xl:sticky xl:top-7 xl:h-fit">
            <div className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
              <label className="text-sm font-semibold text-[#0b1f3a]" htmlFor="employer-guide-search">
                Find a page or action
              </label>
              <div className="relative mt-3">
                <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="employer-guide-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search jobs, offers, KYC..."
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-4 focus:ring-sky-50"
                />
              </div>

              <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                Showing <span className="font-semibold text-[#0b1f3a]">{visiblePages}</span> of{' '}
                <span className="font-semibold text-[#0b1f3a]">{totalPages}</span> topics.
              </div>

              <nav className="mt-5 max-h-[62vh] space-y-2 overflow-y-auto pr-1">
                {filteredSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <a
                      key={section.key}
                      href={`#section-${section.key}`}
                      className="group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-[#0b1f3a]"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition group-hover:bg-[#0b1f3a] group-hover:text-white">
                        <Icon size={18} />
                      </span>
                      <span className="min-w-0 flex-1 truncate">{section.name}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                        {section.pages.length}
                      </span>
                    </a>
                  );
                })}
              </nav>
            </div>
          </aside>

          <main className="space-y-7">
            {filteredSections.length ? (
              filteredSections.map((section) => {
                const Icon = section.icon;
                return (
                  <section key={section.key} id={`section-${section.key}`} className="scroll-mt-24 space-y-4">
                    <div className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
                      <div className="flex items-start gap-4">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                          <Icon size={22} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">Dashboard section</p>
                          <h2 className="mt-1 font-display text-2xl font-normal tracking-tight text-[#0b1f3a]">{section.name}</h2>
                          <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">{section.summary}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {section.pages.map((page) => (
                        <GuideCard key={`${page.title}-${page.path}`} page={page} />
                      ))}
                    </div>
                  </section>
                );
              })
            ) : (
              <div className="rounded-[22px] border border-slate-200 bg-white p-8 text-center shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                  <PiQuestionLight size={22} />
                </span>
                <h2 className="mt-4 font-display text-xl font-normal text-[#0b1f3a]">No guide topic found</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">
                  Try another word, such as matches, job, offer, KYC, interview, dashboard, report, or profile.
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default EmployerGuidePage;
